/**
 * AI Conversation API Service
 *
 * Provides API methods for managing conversations with AI agents including
 * CRUD operations, messaging, and streaming responses via SSE.
 */

import { api, handleApiError } from '../client'
import type {
  Conversation,
  ConversationDetail,
  CreateConversationRequest,
  UpdateConversationRequest,
  ConversationSearchParams,
  Message,
  SendMessageRequest,
  StreamMessageRequest,
  SearchMessagesRequest,
  MessageSearchResult,
  StreamedMessageEvent,
} from '../types'

export const conversationService = {
  // ==================== Conversation Methods ====================

  /**
   * Gets conversations with optional filtering and pagination.
   */
  async getConversations(params?: ConversationSearchParams): Promise<Conversation[]> {
    try {
      const searchParams = new URLSearchParams()

      if (params?.agentId) searchParams.append('agentId', params.agentId)
      if (params?.status) searchParams.append('status', params.status)
      if (params?.activeOnly !== undefined)
        searchParams.append('activeOnly', String(params.activeOnly))
      if (params?.skip !== undefined) searchParams.append('skip', String(params.skip))
      if (params?.take !== undefined) searchParams.append('take', String(params.take))

      const queryString = searchParams.toString()
      const url = `/v1/conversations${queryString ? `?${queryString}` : ''}`

      const response = await api.get<Conversation[]>(url)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a single conversation by ID with messages.
   */
  async getConversation(id: string, skip = 0, take = 50): Promise<ConversationDetail> {
    try {
      const response = await api.get<ConversationDetail>(
        `/v1/conversations/${id}?skip=${skip}&take=${take}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Creates a new conversation with an agent.
   */
  async createConversation(data: CreateConversationRequest): Promise<Conversation> {
    try {
      const response = await api.post<Conversation>('/v1/conversations', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates a conversation's title or status.
   */
  async updateConversation(id: string, data: UpdateConversationRequest): Promise<Conversation> {
    try {
      const response = await api.put<Conversation>(`/v1/conversations/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Archives a conversation (soft delete).
   */
  async archiveConversation(id: string): Promise<void> {
    try {
      await api.put(`/v1/conversations/${id}`, { status: 'Archived' })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Completes a conversation.
   */
  async completeConversation(id: string): Promise<Conversation> {
    try {
      const response = await api.put<Conversation>(`/v1/conversations/${id}`, {
        status: 'Completed',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Message Methods ====================

  /**
   * Sends a message to a conversation and gets the agent's response.
   */
  async sendMessage(data: SendMessageRequest): Promise<Message> {
    try {
      const response = await api.post<Message>('/v1/messages', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Searches messages by content using semantic search.
   */
  async searchMessages(params: SearchMessagesRequest): Promise<MessageSearchResult[]> {
    try {
      const searchParams = new URLSearchParams()
      searchParams.append('query', params.query)
      if (params.conversationId) searchParams.append('conversationId', params.conversationId)
      if (params.limit) searchParams.append('limit', String(params.limit))

      const response = await api.get<MessageSearchResult[]>(
        `/v1/messages/search?${searchParams.toString()}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Streaming Methods ====================

  /**
   * Streams a message response via Server-Sent Events.
   * Returns an EventSource that emits StreamedMessageEvent objects.
   *
   * @param conversationId The conversation ID
   * @param data The message content and optional parent message ID
   * @param onEvent Callback for each stream event
   * @param onError Callback for errors
   * @returns A function to close the stream
   */
  streamMessage(
    conversationId: string,
    data: StreamMessageRequest,
    onEvent: (event: StreamedMessageEvent) => void,
    onError?: (error: Error) => void
  ): () => void {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5018/api'
    const token = localStorage.getItem('access_token')

    // Create the URL for the stream endpoint
    const url = `${baseUrl}/v1/messages/${conversationId}/stream`

    // Use fetch with ReadableStream for SSE with POST
    const controller = new AbortController()

    const fetchStream = async () => {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify(data),
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(`Stream request failed: ${response.status} ${response.statusText}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body')
        }

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            break
          }

          buffer += decoder.decode(value, { stream: true })

          // Process complete lines
          const lines = buffer.split('\n')
          buffer = lines.pop() || '' // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6).trim()
              if (jsonStr && jsonStr !== '[DONE]') {
                try {
                  const event = JSON.parse(jsonStr) as StreamedMessageEvent
                  onEvent(event)
                } catch {
                  // Skip invalid JSON
                }
              }
            }
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          onError?.(error)
        }
      }
    }

    fetchStream()

    // Return cleanup function
    return () => {
      controller.abort()
    }
  },
}
